import React from 'react';
import axios from 'axios';
import { 
  FileText, Trash2, ExternalLink, Save, UserPlus, Users,
  ChevronDown, AlertCircle, GitMerge, Mic, MicOff, Loader2,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Separator } from '../../components/ui/separator';
import QuickDemographicsTile from './QuickDemographicsTile';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../../components/ui/collapsible';
import HourglassIcon from './HourglassIcon';
import MergeNoteModal from './MergeNoteModal';
import { EncounterContextPills } from './EncounterContextPills';
import TeammateDraftChip from './TeammateDraftChip';
import SmartContextCarryBanner from './SmartContextCarryBanner';
import { PATIENT_STATUSES } from '../../constants/mediscribe';
import { useConnectionStatus } from '../../hooks/useConnectionStatus';
import { persistProfilePref } from '../../utils/persistProfilePref';

/**
 * Provider Mode Input Card — extracted from App.js
 * Contains: Patient Label, Demographics, Reason for Visit, Chief Complaint, Notes, Toggle Section, Action Buttons
 */
const ProviderInputCard = ({
  // Notes
  notes, setNotes, recording, notesHistory, undoNotes,
  // Text size
  textSizeLevel,
  // Force single column
  forceSingleColumn,
  // Generate
  generateDocuments, isGenerating,
  // User (Feb 26, 2026 — used by MedicModeRow to compare against profile default)
  user,
  // Clear / draft / next
  clearAll, saveDraft, nextPatient,
  effectiveRole,
  // Patient Label
  PatientLabelSection, patientLabel, setPatientLabel, clinicSchedule, setClinicSchedule,
  // Provider Demographics
  ProviderDemographics, patientAge, setPatientAge, patientGender, setPatientGender,
  encounterType, setEncounterType, visitType, setVisitType,
  panelOwnership, setPanelOwnership, appointmentType, setAppointmentType,
  patientStatus, setPatientStatus, autoFilledFields, setAutoFilledFields,
  setIncludePE, setPeUnremarkable,
  peExceptions, setPeExceptions,
  // Deferred Topics
  deferredTopics, setDeferredTopics,
  // Reason for visit
  // Feb 27, 2026 — When true, the wide PatientSetupHeader above the 2-col
  // grid owns the Quick Demographics tile, Reason for Visit chips, and
  // Chief Complaint input. ProviderInputCard hides those sections to
  // prevent duplicate fields.
  setupInHeader = false,
  reasonForVisit, reasonAutoDetected, sortedReasonOptions,
  setReasonManually, customReasonForVisit, setCustomReasonManually,
  // Chief Complaint
  ChiefComplaintSection, chiefComplaint, setChiefComplaint,
  // Notes Input
  NotesInput,
  // Toggle section
  ProviderToggleSection, toggleSectionProps,
  // Action buttons
  ActionButtons,
  // Detected ICD-10 codes
  DetectedICD10Inline, autoDetectedCodes, manualIcd10, setManualIcd10,
  // Virtual modality
  virtualModality, setVirtualModality,
  // Pickup audit trail (relay-race pill)
  pickupMeta, setPickupMeta,
  // Re-Baton — hand the current draft back to the same team the original
  // baton came from (Feb 27, 2026). Null when no team_id on pickupMeta.
  onReBaton,
  // Smart Context Carry inputs (Phase 3)
  history, currentDraftId,
  // Smart paste demographics extractor (Feb 16 UX #5)
  onSmartPaste,
  // DOS (Date of Service) input — Feb 24, 2026 fix.
  // Provider can now set the DOS BEFORE generating so they don't have to
  // wait for the output to finish before correcting back-charting dates.
  dosOverride, setDosOverride,
  // Medic Mode toggle — Feb 24, 2026. Per-note override; renders a compact
  // checkbox in the input card. When checked, generated note is HPI + ROS +
  // ICD-10 handoff only (no A/P, no Education).
  medicMode, setMedicMode,
  // "At Your Last Visit" merge box (Feb 25, 2026). Optional historical-context
  // textarea — passes to backend as `prior_visit_context` so the LLM treats
  // anything pasted here as HISTORICAL, never as today's findings.
  priorVisitContext, setPriorVisitContext,
  // v6.8.44 — Saved Injuries (patient mode chip-picker above textarea)
  savedInjuries,
  // Pre-Visit Prep / RN Handoff (Feb 26, 2026). Captured BEFORE walking into
  // the room. Backend treats as HANDOFF CONTEXT — shapes orders/workup but
  // is never cited as "patient reports / on exam."
  prepNotes, setPrepNotes,
}) => {
  const [showMergeModal, setShowMergeModal] = React.useState(false);

  const handleMergeText = (text) => {
    setNotes((prev) => {
      const cur = (prev || '').trim();
      if (!cur) return text.trim();
      return `${cur}\n\n--- TEAMMATE INPUT ---\n${text.trim()}`;
    });
    toast.success('Teammate notes merged. Review & generate when ready.');
  };

  // When a teammate draft chip is picked up, replace input with the draft's
  // notes AND stamp pickup metadata for the audit pill.
  const handlePickupDraft = (draft) => {
    if (draft?.notes) setNotes(draft.notes);
    if (setPickupMeta) {
      setPickupMeta({
        from: draft.shared_by_name || draft.author_name || 'teammate',
        at: draft.shared_at || draft.updated_at || new Date().toISOString(),
        patient_label: draft.title || draft.patient_label || null,
      });
    }
  };

  // Offline-aware mic: Deepgram needs network. When offline, disable the
  // dictation button with a friendly tooltip (Feb 16, 2026 — Lean Offline Mode MVP).
  const { status: connectionStatus } = useConnectionStatus();
  const isOffline = connectionStatus === 'offline';

  return (
    <Card className="border-border bg-card" data-testid="input-card">
      <CardHeader className="pb-3 px-3 sm:px-6">
        <CardTitle className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-3 text-foreground font-bold text-lg">
          <div className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-primary" />
            <span className="elegant-title text-lg">Your Notes</span>
          </div>
          <div className="flex items-center gap-1 sm:gap-2 flex-wrap justify-end">
            <Button 
              onClick={() => clearAll()} 
              variant="outline" size="sm"
              className="h-8 px-2 sm:px-3 text-xs rounded-none border-2 border-red-500 bg-red-700 text-white hover:bg-red-600"
              data-testid="clear-btn"
              title="Clear - Deletes current work WITHOUT saving"
            >
              <Trash2 className="w-3.5 h-3.5 sm:mr-1.5" />
              <span className="hidden sm:inline">Clear</span>
            </Button>
            
            <Button 
              onClick={() => { 
                window.open(window.location.href, '_self');
                toast.success('New tab opened! This tab stays as-is.', { duration: 2000 });
              }} 
              variant="outline" size="sm"
              className="h-8 px-2 sm:px-3 text-xs rounded-none border-2 border-slate-500 bg-slate-700 text-slate-100 hover:bg-slate-600"
              data-testid="new-tab-btn"
              title="Open new tab (keeps current work intact)"
            >
              <ExternalLink className="w-3.5 h-3.5 sm:mr-1.5" />
              <span className="hidden sm:inline">New Tab</span>
            </Button>
            
            <div className="w-px h-6 bg-border mx-1 hidden sm:block"></div>
            
            <Button 
              onClick={() => saveDraft()} 
              variant="outline" size="sm"
              className="h-8 px-2 sm:px-3 text-xs rounded-none border-2 border-blue-500 bg-blue-700 text-white hover:bg-blue-600 disabled:bg-slate-700 disabled:text-slate-400 disabled:border-slate-600"
              data-testid="save-draft-btn"
              disabled={!notes.trim() || notes.trim().length < 10}
              title="SAVE - Quick save to History, generate docs later"
            >
              <Save className="w-3.5 h-3.5 sm:mr-1.5" />
              <span className="hidden sm:inline">Save</span>
            </Button>
            
            <Button 
              onClick={() => nextPatient()} 
              variant="outline" size="sm"
              className="relative h-8 px-2 sm:px-3 text-xs font-bold rounded-none border-2 border-emerald-300 bg-emerald-600 text-white hover:bg-emerald-500 hover:shadow-lg hover:shadow-emerald-500/30 hover:scale-105 active:scale-95 transition-all shadow-sm shadow-emerald-700/40 disabled:bg-slate-700 disabled:text-slate-400 disabled:border-slate-600"
              data-testid="next-pt-btn"
              disabled={recording.isTranscribing || isGenerating}
              title={recording.isTranscribing ? "Transcribing..." : isGenerating ? "Generating..." : "NEXT PT - Options to save, then clears"}
            >
              <UserPlus className={`w-3.5 h-3.5 sm:mr-1.5 ${recording.isTranscribing || isGenerating ? 'opacity-50' : ''}`} />
              <span className="hidden sm:inline">{recording.isTranscribing ? 'Wait...' : isGenerating ? 'Wait...' : 'Next Pt'}</span>
              {!recording.isTranscribing && !isGenerating && (
                <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-emerald-200 border border-emerald-100/80 shadow shadow-emerald-300/60 animate-pulse" aria-hidden="true" />
              )}
            </Button>
            
            <Button 
              onClick={generateDocuments} 
              disabled={isGenerating || !notes.trim()} 
              size="sm"
              className="h-8 px-2 sm:px-3 text-xs rounded-none border-2 border-amber-300 bg-amber-600 hover:bg-amber-500 text-white font-bold shadow-md hover:shadow-lg transition-all disabled:bg-slate-700 disabled:text-slate-400 disabled:border-slate-600"
              data-testid="create-docs-btn-top"
              title={isGenerating ? "Generating..." : (notes?.trim() ? "Generate" : "Type or dictate notes first")}
            >
              {isGenerating ? <><HourglassIcon className="w-4 h-4 sm:mr-1.5" /><span className="hidden sm:inline">Generating...</span></> : <><img src="/emblem_icon.png" alt="" className="w-6 h-6 sm:mr-1.5 object-contain inline-block" /><span className="hidden sm:inline">Generate</span></>}
            </Button>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className={`${setupInHeader ? 'flex flex-col gap-4' : 'space-y-4'}`}>
        {/* Mobile Notice */}
        <div className="mobile-notice sm:hidden">
          <AlertCircle className="w-4 h-4 mobile-notice-icon flex-shrink-0" />
          <span className="mobile-notice-text">For best experience, rotate to landscape or use a larger screen. All features work, but layout is optimized for wider displays.</span>
        </div>

        {/* Patient Label */}
        <div className="pb-2 border-b border-border/50">
          <PatientLabelSection
            patientLabel={patientLabel}
            setPatientLabel={setPatientLabel}
            clinicSchedule={clinicSchedule}
            setClinicSchedule={setClinicSchedule}
            notes={notes}
            chiefComplaint={chiefComplaint}
          />
        </div>

        {/* Mar 2, 2026 — Pre-Encounter context pills previously sat right
            below the patient label. Provider asked to move them BETWEEN
            demographics/visit-details and the notes textarea (chronologic
            order: patient → visit setup → pre-encounter context → dictation).
            Actual render is now further down, right before NotesInput.
            Keeping this comment breadcrumb only. */}

        {/* DOS (Date of Service) — Feb 24, 2026.
            Quick back-charting fix. Provider can set the DOS BEFORE generating
            (and committing Copy All) instead of having to wait for the output
            to render and revise the sig block. Empty = today's date. */}
        {effectiveRole === 'provider' && setDosOverride && (
          <div className="flex items-center gap-2 -mt-1 pb-2 border-b border-border/50" data-testid="input-dos-picker-row">
            <Label htmlFor="input-dos-picker" className="text-[10px] text-muted-foreground shrink-0">
              DOS
            </Label>
            <Input
              id="input-dos-picker"
              type="date"
              value={dosOverride || ''}
              onChange={(e) => setDosOverride(e.target.value)}
              className="h-7 w-[160px] text-xs"
              data-testid="input-dos-picker"
              title="Override Date of Service (use for back-charting). Leave empty for today."
            />
            {dosOverride && (
              <>
                <span className="text-[10px] text-amber-400 font-bold" data-testid="input-dos-manual-tag">
                  MANUAL DOS
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2 text-[10px] text-muted-foreground hover:text-foreground"
                  onClick={() => setDosOverride('')}
                  data-testid="input-dos-reset-btn"
                  title="Reset DOS to today"
                >
                  Reset
                </Button>
              </>
            )}
            {!dosOverride && (
              <span className="text-[10px] text-muted-foreground italic">
                (defaults to today — set here for back-charting)
              </span>
            )}
          </div>
        )}

        {/* Medic Mode toggle — Feb 24, 2026. Compact per-note checkbox so a
            medic / 68W can flip into HPI+ROS+ICD-10 handoff mode without
            opening Profile Settings.
            Feb 26, 2026 — when the user flips the toggle to a value that
            differs from their profile default, a tiny "Save as default" pill
            appears for 6s offering one-tap profile persistence. */}
        {effectiveRole === 'provider' && typeof setMedicMode === 'function' && (
          <MedicModeRow
            medicMode={medicMode}
            setMedicMode={setMedicMode}
            user={user}
          />
        )}

        {/* Demo Mode seeder — Feb 26, 2026.
            Only renders when Demo Mode is enabled in Profile Settings.
            One tap fills the textarea with a realistic demo encounter so
            the provider can pitch / train without burning a real chart. */}
        {effectiveRole === 'provider' && (
          <DemoModeSeeder
            setNotes={setNotes}
            setPatientAge={setPatientAge}
            setPatientGender={setPatientGender}
            setEncounterType={setEncounterType}
          />
        )}

        {/* Pre-Encounter pills moved above (right below patient label).
            Kept this comment as a breadcrumb — Mar 2, 2026. */}

        {/* Demographics - Collapsible
            Feb 27, 2026 — Hidden when setupInHeader is true. Visit Type
            and Appointment Type both live in PatientSetupHeader above; the
            remaining Panel Owner / DOS Time fields are accessible from
            Profile / Templates as needed. */}
        {!setupInHeader && (
        <Collapsible defaultOpen={false}>
          <CollapsibleTrigger asChild>
            <Button variant="ghost" size="sm" className="w-full justify-between text-muted-foreground hover:text-foreground h-7 -mt-1">
              <span className="flex items-center gap-2 text-xs">
                <Users className="w-3.5 h-3.5" />
                Visit Details
                {(patientStatus || appointmentType || visitType) && (
                  <span className="text-[10px] text-primary">
                    {[
                      patientStatus && PATIENT_STATUSES.find(s => s.value === patientStatus)?.label,
                      appointmentType,
                      visitType,
                    ].filter(Boolean).join(' · ')}
                  </span>
                )}
              </span>
              <ChevronDown className="w-3.5 h-3.5" />
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <ProviderDemographics
              patientAge={patientAge} setPatientAge={setPatientAge}
              patientGender={patientGender} setPatientGender={setPatientGender}
              encounterType={encounterType} setEncounterType={setEncounterType}
              visitType={visitType} setVisitType={setVisitType}
              panelOwnership={panelOwnership} setPanelOwnership={setPanelOwnership}
              appointmentType={appointmentType} setAppointmentType={setAppointmentType}
              reasonForVisit={reasonForVisit}
              setReasonForVisit={setReasonManually}
              customReasonForVisit={customReasonForVisit}
              setCustomReasonForVisit={setCustomReasonManually}
              patientStatus={patientStatus} setPatientStatus={setPatientStatus}
              autoFilledFields={autoFilledFields} setAutoFilledFields={setAutoFilledFields}
              setIncludePE={setIncludePE} setPeUnremarkable={setPeUnremarkable}
            />
          </CollapsibleContent>
        </Collapsible>
        )}

        {/* Reason for Visit + Chief Complaint
            Feb 27, 2026 — wrapped in a setupInHeader gate so the wide
            PatientSetupHeader can own these without duplication. */}
        {/* Reason for Visit + Chief Complaint
            Feb 27, 2026 — wrapped in a setupInHeader gate so the wide
            PatientSetupHeader can own these without duplication. */}
        {!setupInHeader && (
          <>
            <Separator className="bg-border" />
            <div className="space-y-1" data-testid="reason-for-visit-section">
          <div className="flex items-center justify-between">
            <Label className="text-[10px] text-muted-foreground">Reason for Visit</Label>
            <span className="text-[9px] text-muted-foreground/70">Tip: select multiple (e.g., Annual + Lab Review)</span>
          </div>
          {(() => {
            // Feb 18, 2026 — Multi-select Reason for Visit. Stored as a
            // comma-separated string for backwards compatibility (single
            // value still works everywhere downstream). Auto-detect only
            // touches the initial "other" state so it doesn't fight with
            // explicit multi-selection.
            const selectedValues = (reasonForVisit || '').split(',').map(v => v.trim()).filter(Boolean);
            const labelFor = (val) => (sortedReasonOptions.find(o => o.value === val)?.label) || val;
            const isOther = selectedValues.includes('other');
            const isEmpty = selectedValues.length === 0;
            const toggle = (val) => {
              let next;
              if (selectedValues.includes(val)) {
                next = selectedValues.filter(v => v !== val);
              } else {
                // Selecting any real reason auto-removes the "other" sentinel
                next = [...selectedValues.filter(v => v !== 'other'), val];
              }
              if (next.length === 0) next = ['other'];
              setReasonManually(next.join(','));
            };
            const remove = (val) => {
              const next = selectedValues.filter(v => v !== val);
              setReasonManually(next.length === 0 ? 'other' : next.join(','));
            };
            return (
              <div className={`flex flex-wrap gap-1.5 items-center p-1.5 border rounded-md bg-input ${reasonAutoDetected ? 'ring-1 ring-amber-400/50' : 'border-border'}`}>
                {/* Selected pills */}
                {!isEmpty && !isOther && selectedValues.map((val) => (
                  <span
                    key={val}
                    className="inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-full bg-primary/15 border border-primary/30 text-[11px] font-medium text-primary"
                    data-testid={`reason-pill-${val}`}
                  >
                    {labelFor(val)}
                    <button
                      type="button"
                      onClick={() => remove(val)}
                      className="ml-0.5 w-3.5 h-3.5 rounded-full hover:bg-primary/30 flex items-center justify-center"
                      title="Remove"
                      data-testid={`reason-pill-remove-${val}`}
                    >
                      <span className="text-[10px] leading-none">×</span>
                    </button>
                  </span>
                ))}
                {/* + Add popover */}
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-dashed border-border bg-secondary/30 text-[10px] font-semibold text-muted-foreground hover:text-foreground hover:border-primary/40 transition-colors"
                      data-testid="reason-for-visit-add"
                    >
                      {isEmpty || isOther ? '+ Select reason(s)' : '+ Add another'}
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-72 p-0 max-h-72 overflow-y-auto" align="start">
                    <div className="sticky top-0 bg-card/95 backdrop-blur-sm px-3 py-2 border-b border-border">
                      <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                        Pick one or more · {selectedValues.filter(v => v !== 'other').length} selected
                      </p>
                    </div>
                    <ul className="py-1">
                      {sortedReasonOptions.filter(o => o.value !== 'other').map((opt) => {
                        const checked = selectedValues.includes(opt.value);
                        return (
                          <li key={opt.value}>
                            <button
                              type="button"
                              onClick={() => toggle(opt.value)}
                              className={`w-full flex items-center gap-2 px-3 py-1.5 text-xs text-left transition-colors ${checked ? 'bg-primary/10 text-primary font-medium' : 'hover:bg-secondary/40 text-foreground'}`}
                              data-testid={`reason-option-${opt.value}`}
                            >
                              <span className={`shrink-0 w-3.5 h-3.5 rounded border flex items-center justify-center ${checked ? 'border-primary/60 bg-primary/20' : 'border-border bg-background'}`}>
                                {checked && <span className="text-[10px] leading-none">✓</span>}
                              </span>
                              {opt.label}
                            </button>
                          </li>
                        );
                      })}
                      <li className="border-t border-border mt-1 pt-1">
                        <button
                          type="button"
                          onClick={() => setReasonManually('other')}
                          className={`w-full flex items-center gap-2 px-3 py-1.5 text-xs text-left transition-colors ${isOther ? 'bg-amber-500/15 text-amber-300 font-medium' : 'hover:bg-secondary/40 text-foreground'}`}
                          data-testid="reason-option-other"
                        >
                          <span className={`shrink-0 w-3.5 h-3.5 rounded border flex items-center justify-center ${isOther ? 'border-amber-400/60 bg-amber-500/20' : 'border-border bg-background'}`}>
                            {isOther && <span className="text-[10px] leading-none">✓</span>}
                          </span>
                          Other / Not specified  (type a custom reason)
                        </button>
                      </li>
                    </ul>
                  </PopoverContent>
                </Popover>
                {isOther && (
                  <Input
                    type="text"
                    placeholder="Specify reason for visit..."
                    value={customReasonForVisit || ''}
                    onChange={(e) => setCustomReasonManually(e.target.value)}
                    className="h-7 text-xs bg-background border-border flex-1 min-w-[200px]"
                    data-testid="custom-reason-input"
                  />
                )}
              </div>
            );
          })()}
        </div>
        <ChiefComplaintSection
          chiefComplaint={chiefComplaint}
          setChiefComplaint={setChiefComplaint}
          autoFilledFields={autoFilledFields}
          setAutoFilledFields={setAutoFilledFields}
          onGenerateDocs={generateDocuments}
          isGenerating={isGenerating}
          hasNotes={!!notes.trim()}
        />
          </>
        )}

        {/* Visit Type Toggle + Virtual Modality */}
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-1.5" data-testid="standalone-visit-toggle">
            <span className="text-[10px] text-muted-foreground font-medium">Visit:</span>
            <div className="flex gap-0.5 bg-secondary rounded-md p-0.5">
              <button 
                onClick={() => {
                  setVisitType(visitType === 'results_review_virtual' ? 'results_review' : 'in_person_exam');
                  setIncludePE(true);
                  setPeUnremarkable(true);
                }}
                className={`px-2.5 py-1 rounded text-[10px] font-semibold transition-all whitespace-nowrap ${
                  visitType !== 'virtual' && visitType !== 'results_review_virtual'
                    ? 'bg-primary text-primary-foreground shadow-sm' 
                    : 'text-muted-foreground hover:text-foreground'
                }`}
                data-testid="standalone-toggle-in-person"
              >In-Person</button>
              <button 
                onClick={() => {
                  setVisitType(visitType === 'results_review' ? 'results_review_virtual' : 'virtual');
                  setIncludePE(false);
                  setPeUnremarkable(false);
                }}
                className={`px-2.5 py-1 rounded text-[10px] font-semibold transition-all whitespace-nowrap ${
                  visitType === 'virtual' || visitType === 'results_review_virtual'
                    ? 'bg-blue-500 text-white shadow-sm' 
                    : 'text-muted-foreground hover:text-foreground'
                }`}
                data-testid="standalone-toggle-virtual"
              >Virtual</button>
            </div>
          </div>
          {/* Video / Telephone sub-toggle — only when Virtual is selected */}
          {(visitType === 'virtual' || visitType === 'results_review_virtual') && (
            <div className="flex items-center gap-1.5" data-testid="virtual-modality-toggle">
              <div className="flex gap-0.5 bg-secondary rounded-md p-0.5">
                <button
                  onClick={() => { setVirtualModality('video'); localStorage.setItem('writeright_virtual_modality', 'video'); persistProfilePref({ virtual_modality: 'video' }); }}
                  className={`px-2.5 py-1 rounded text-[10px] font-semibold transition-all whitespace-nowrap ${
                    virtualModality === 'video' ? 'bg-blue-500 text-white shadow-sm' : 'text-muted-foreground hover:text-foreground'
                  }`}
                  data-testid="input-modality-video"
                >Video</button>
                <button
                  onClick={() => { setVirtualModality('telephone'); localStorage.setItem('writeright_virtual_modality', 'telephone'); persistProfilePref({ virtual_modality: 'telephone' }); }}
                  className={`px-2.5 py-1 rounded text-[10px] font-semibold transition-all whitespace-nowrap ${
                    virtualModality === 'telephone' ? 'bg-blue-500 text-white shadow-sm' : 'text-muted-foreground hover:text-foreground'
                  }`}
                  data-testid="input-modality-telephone"
                >Phone</button>
              </div>
            </div>
          )}
          <div className="w-px h-5 bg-border/50" />
        </div>
        <ProviderToggleSection {...toggleSectionProps} />

        {/* Smart Context Carry — detect follow-up + offer to carry prior A/P */}
        {effectiveRole === 'provider' && (
          <SmartContextCarryBanner
            notes={notes}
            reasonForVisit={reasonForVisit}
            patientAge={patientAge}
            patientGender={patientGender}
            history={history}
            setNotes={setNotes}
            currentDraftId={currentDraftId}
          />
        )}

        {/* Recently Picked Up — relay-race audit pill (visible only after pickup) */}
        {effectiveRole === 'provider' && pickupMeta && (
          <div
            className="flex items-center gap-2 px-3 py-1.5 rounded-md border border-amber-500/40 bg-amber-500/10 text-[11px] text-amber-200"
            data-testid="pickup-meta-pill"
          >
            <span className="text-amber-400 text-base leading-none">↪</span>
            <span className="font-semibold">Picked up</span>
            <span className="text-amber-300/80">from</span>
            <span className="font-mono text-amber-100">{pickupMeta.from}</span>
            {pickupMeta.at && (
              <>
                <span className="text-amber-400/60">·</span>
                <span className="text-amber-300/80">
                  {(() => {
                    try {
                      const d = new Date(pickupMeta.at);
                      return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
                    } catch { return ''; }
                  })()}
                </span>
              </>
            )}
            {pickupMeta.patient_label && (
              <>
                <span className="text-amber-400/60">·</span>
                <span className="text-amber-300/70 truncate max-w-[180px]">{pickupMeta.patient_label}</span>
              </>
            )}
            {/* Re-Baton — Feb 27, 2026. One-tap "hand back to the same team"
                so the relay race round-trips without leaving the input
                view. Only renders when we know which team the baton came
                from (team_id stashed on pickupMeta at pickup time). */}
            {pickupMeta.team_id && typeof onReBaton === 'function' && (
              <button
                type="button"
                onClick={onReBaton}
                className="ml-auto px-2 py-0.5 rounded-md border border-emerald-400/60 bg-emerald-500/15 text-emerald-200 text-[10px] font-semibold hover:bg-emerald-500/25 hover:text-emerald-100 transition-colors"
                title={`Save current draft and hand it back to ${pickupMeta.team_name || 'the same team'}`}
                data-testid="pickup-meta-rebaton"
              >
                ↩ Hand back{pickupMeta.team_name ? ` to ${pickupMeta.team_name}` : ''}
              </button>
            )}
            <button
              type="button"
              onClick={() => setPickupMeta && setPickupMeta(null)}
              className={`${pickupMeta.team_id ? '' : 'ml-auto'} text-amber-400/60 hover:text-amber-300 transition-colors text-sm leading-none`}
              title="Dismiss pickup pill"
              data-testid="pickup-meta-dismiss"
            >
              ✕
            </button>
          </div>
        )}

        {/* Teammate Draft Chip — surfaces pickable batons above the input */}
        {effectiveRole === 'provider' && (
          <div className="flex items-center gap-2 flex-wrap">
            <TeammateDraftChip onPickup={handlePickupDraft} />
          </div>
        )}

        {/* Quick Demographics Tile (Feb 26, 2026) — surfaces Age + Sex +
            Encounter Type above the notes textarea so the provider doesn't
            have to expand the buried demographics drawer. Same setters,
            single source of truth.
            Feb 27, 2026 — hidden when the wide PatientSetupHeader is
            rendering this tile above the 2-col grid. */}
        {effectiveRole === 'provider' && !setupInHeader && (
          <QuickDemographicsTile
            patientAge={patientAge}
            setPatientAge={setPatientAge}
            patientGender={patientGender}
            setPatientGender={setPatientGender}
            encounterType={encounterType}
            setEncounterType={setEncounterType}
          />
        )}

        {/* Notes Input
            Feb 27, 2026 — When setupInHeader is true, NotesInput renders
            with order-first so it appears at the TOP of the input column.
            Setup chrome (Daily Schedule, At Your Last Visit, Pre-Visit
            Prep, Output toggles, ICD-10, PE) lives below the textarea. */}
        <div className={setupInHeader ? 'order-first' : ''}>
        {/* v6.8.44 — My Injuries Picker (patient mode only).
            Renders a 1-tap chip row above the textarea so the patient
            can inject any saved condition into their dictation when
            drafting letters/appeals. Wired via App.js props. */}
        {effectiveRole === 'patient' && Array.isArray(savedInjuries) && savedInjuries.length > 0 && (
          <div className="mb-2 p-2 rounded-md border border-rose-500/20 bg-rose-500/[0.04]" data-testid="my-injuries-picker">
            <div className="flex items-center gap-2 mb-1.5">
              <span className="text-[10px] font-bold text-rose-300/90 uppercase tracking-wider">Pick from my saved injuries</span>
              <span className="text-[9px] text-muted-foreground italic">tap a chip to add to your situation</span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {savedInjuries.filter(i => i?.label).map((inj) => (
                <button
                  key={inj.id}
                  type="button"
                  onClick={() => {
                    const txt = inj.year ? `${inj.label} (${inj.year})` : inj.label;
                    const sep = (notes || '').trim() ? '\n' : '';
                    setNotes((notes || '') + sep + txt);
                  }}
                  className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors
                    ${inj.active
                      ? 'border-rose-500/40 bg-rose-500/10 text-rose-200 hover:bg-rose-500/20'
                      : 'border-border/40 bg-secondary/30 text-muted-foreground hover:bg-secondary/50'}`}
                  data-testid={`my-injury-chip-${inj.id}`}
                  title={inj.active ? 'Active flare — tap to add to your situation' : 'Inactive — tap to add anyway'}
                >
                  {inj.active && <span className="text-rose-400 mr-1">●</span>}
                  {inj.label}
                  {inj.year && <span className="text-muted-foreground/70 ml-1">({inj.year})</span>}
                </button>
              ))}
            </div>
          </div>
        )}
        {/* Mar 2, 2026 — Pre-Encounter pills relocated here per provider's
            arrow drawing: chronologically they belong AFTER demographics/
            visit-details and IMMEDIATELY BEFORE the dictation textarea. */}
        {effectiveRole === 'provider' && (typeof setPriorVisitContext === 'function' || typeof setPrepNotes === 'function') && (
          <EncounterContextPills
            priorVisitContext={priorVisitContext}
            setPriorVisitContext={setPriorVisitContext}
            prepNotes={prepNotes}
            setPrepNotes={setPrepNotes}
            onPickUpBaton={() => setShowMergeModal(true)}
          />
        )}
        <NotesInput
          effectiveRole={effectiveRole}
          notes={notes}
          setNotes={setNotes}
          recording={recording}
          notesHistory={notesHistory}
          undoNotes={undoNotes}
          onGenerateDocs={generateDocuments}
          isGenerating={isGenerating}
          onSmartPaste={onSmartPaste}
        />
        </div>

        {/* Next Visit / Deferred Topics (Z71.89) */}
        {effectiveRole === 'provider' && (
          <div className="space-y-1" data-testid="deferred-topics-section">
            <label className="text-[10px] font-bold text-amber-400/80 uppercase tracking-wider cursor-pointer flex items-center gap-1"
              onClick={() => {
                const el = document.getElementById('deferred-topics-input');
                if (el) el.focus();
              }}
            >
              Next Visit Topics
              <span className="text-[8px] font-normal text-muted-foreground/60">(Z71.89 - optional)</span>
            </label>
            <textarea
              id="deferred-topics-input"
              value={deferredTopics || ''}
              onChange={(e) => setDeferredTopics(e.target.value)}
              placeholder="e.g. Discuss migraine management, review sleep study results, follow up on PT progress..."
              className="w-full min-h-[32px] max-h-[80px] px-2 py-1.5 rounded text-xs bg-input border border-amber-500/20 text-foreground placeholder:text-muted-foreground/50 resize-y focus:border-amber-500/50 focus:ring-1 focus:ring-amber-500/20 transition-colors"
              data-testid="deferred-topics-input"
              rows={1}
            />
          </div>
        )}

        {/* Merge Teammate Input + Dictate.
            Feb 28, 2026 — when notes are empty, the Pick-Up-Baton action is
            surfaced as a header pill in EncounterContextPills (top of card).
            Bottom button collapses to a pure "Merge" affordance once notes
            exist, so the workflow becomes:
              (no notes yet)  → top pill "Pick Up Baton"
              (drafting)      → bottom slim "Merge" pill
            Reduces visual repetition the user flagged as "chunky / kindergarten". */}
        <div className="flex gap-2 items-stretch flex-wrap">
          {notes.trim() && (
            <Button
              onClick={() => setShowMergeModal(true)}
              variant="outline"
              size="sm"
              className="h-8 px-3 text-xs border-amber-500/40 text-amber-400 hover:bg-amber-500/10 font-semibold gap-1.5 rounded-full"
              data-testid="merge-input-btn"
              title="Append teammate notes / Genesis paste"
            >
              <GitMerge className="w-3.5 h-3.5" />
              Merge
            </Button>
          )}
          {notes.trim() && (
            <Button
              onClick={recording.isRecording ? recording.stopRecording : recording.startRecording}
              disabled={recording.isTranscribing || isOffline}
              variant="outline"
              className={`h-12 px-4 text-sm font-semibold gap-2 transition-all ${
                recording.isRecording
                  ? 'border-red-500/60 text-red-400 hover:bg-red-500/10 ring-1 ring-red-500/30'
                  : isOffline
                    ? 'border-slate-600/60 text-slate-400 cursor-not-allowed opacity-60'
                    : 'border-blue-500/40 text-blue-400 hover:bg-blue-500/10'
              }`}
              data-testid="merge-dictate-btn"
              data-offline={isOffline ? 'true' : undefined}
              title={isOffline
                ? 'Dictation paused — needs internet. Type notes manually; your draft auto-saves.'
                : recording.isRecording ? 'Stop dictation' : 'Start dictation'}
            >
              {recording.isTranscribing ? (
                <><Loader2 className="w-4 h-4 animate-spin" />Wait...</>
              ) : recording.isRecording ? (
                <><MicOff className="w-4 h-4" />Stop</>
              ) : isOffline ? (
                <><MicOff className="w-4 h-4" />Offline</>
              ) : (
                <><Mic className="w-4 h-4" />Dictate</>
              )}
            </Button>
          )}
          {/* Recording Mode Selector (Feb 26, 2026)
              Solo = single-speaker dictation (no diarization, fastest)
              In-Person = two-speaker, in-room — Deepgram speaker labels
              Phone = two-speaker phone — labels turns as Provider/Patient */}
          {notes.trim() && (
            <div
              className="flex items-center gap-1 px-2 h-12 rounded-md border border-blue-500/30 bg-blue-500/5"
              data-testid="recording-mode-group"
              data-mode={recording.recordingMode || 'solo'}
              title="Recording mode — choose Solo for dictation, In-Person for two-speaker rooms, Phone for telecalls (auto-labels Provider/Patient turns)."
            >
              <span className="text-[10px] uppercase tracking-wider text-blue-400/70 font-bold mr-1">Mode</span>
              {[
                { v: 'solo', label: 'Solo', tip: 'Single dictation (no diarization)' },
                { v: 'in_person', label: 'In-Room', tip: 'Two-speaker in-person encounter — speaker labels' },
                { v: 'phone', label: 'Phone', tip: 'Phone call — labels turns as Provider / Patient' },
              ].map((m) => {
                const active = recording.recordingMode === m.v;
                return (
                  <button
                    key={m.v}
                    type="button"
                    onClick={() => recording.setRecordingMode && recording.setRecordingMode(m.v)}
                    disabled={recording.isRecording || recording.isTranscribing}
                    className={`text-[11px] px-2 py-1 rounded font-semibold transition-colors ${
                      active
                        ? 'bg-blue-500/30 text-blue-200 border border-blue-400/60'
                        : 'text-blue-300/70 border border-transparent hover:bg-blue-500/15 hover:text-blue-200'
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                    data-testid={`recording-mode-${m.v}`}
                    title={m.tip}
                  >
                    {m.label}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Detected ICD-10 Codes + Manual Input — above action buttons */}
        {DetectedICD10Inline && (
          <DetectedICD10Inline
            autoDetectedCodes={autoDetectedCodes}
            manualIcd10={manualIcd10}
            setManualIcd10={setManualIcd10}
            chiefComplaint={chiefComplaint}
            reasonForVisit={reasonForVisit}
            notes={notes}
          />
        )}

        {/* Action Buttons */}
        <ActionButtons
          effectiveRole={effectiveRole}
          isGenerating={isGenerating}
          notes={notes}
          generateDocuments={generateDocuments}
          clearAll={clearAll}
          priorVisitContext={priorVisitContext}
          prepNotes={prepNotes}
        />
      </CardContent>

      {/* Merge / Pickup Modal */}
      <MergeNoteModal
        open={showMergeModal}
        onClose={() => setShowMergeModal(false)}
        onMerge={handleMergeText}
        currentNotes={notes}
      />
    </Card>
  );
};

/**
 * DemoModeSeeder — One-tap fake patient + sample dictation seeder.
 * Feb 26, 2026.
 *
 * Renders nothing unless localStorage `writeright_demo_mode === '1'` (toggled
 * from Profile Settings). When visible, a small purple chip says "Demo Mode".
 * Clicking it seeds:
 *   • patientAge=47, patientGender=M, encounterType=established
 *   • Notes = a realistic HTN+HLD+T2DM follow-up dictation
 * Then animates a brief "Demo seeded ✓" confirmation. The chip stays visible
 * so the provider can re-seed for a different scenario.
 *
 * Useful for pitches, training sessions, recording walk-throughs. The data
 * never persists past clearAll/Next Patient.
 */
const DEMO_SCENARIOS = [
  {
    label: 'Chronic — HTN/HLD/DM',
    age: 47, gender: 'M', encounter: 'established',
    notes:
      "47 yo male AD established patient seen today for chronic disease follow-up. " +
      "HTN, HLD, T2DM. BP 148/92 in clinic, home avg 144/88. A1c 8.2% (down from 9.1% in October). " +
      "LDL 165. Currently on lisinopril 20mg daily, metformin 1000mg BID. No statin yet. " +
      "Denies chest pain, SOB, vision changes, polyuria. Endorses morning fatigue x 2 weeks. " +
      "Increased lisinopril to 40mg daily, starting atorvastatin 40mg qHS, continuing metformin. " +
      "Discussed lifestyle changes, low-sodium diet, BP log. Follow up in 8 weeks."
  },
  {
    label: 'Acute — URI / Strep',
    age: 28, gender: 'F', encounter: 'acute',
    notes:
      "28 yo female AD presents with 3-day history of sore throat, fever to 101.4, " +
      "and bilateral tender anterior cervical lymphadenopathy. Endorses dysphagia, " +
      "denies cough, rhinorrhea, conjunctivitis. PE: tonsils 3+ enlarged with white exudate, " +
      "no peritonsillar bulge. Centor score 4. Rapid strep positive in clinic. " +
      "Starting amoxicillin 500mg TID x 10 days. Tylenol/ibuprofen for pain, salt water gargles. " +
      "Return if no improvement in 48h or any difficulty breathing."
  },
  {
    label: 'Ortho — Knee pain',
    age: 34, gender: 'M', encounter: 'new',
    notes:
      "34 yo male AD soldier presents with R knee pain x 1 week after fall during PT. " +
      "Pain 6/10 medial joint line, worse with stairs and pivoting. Denies locking, " +
      "buckling, popping at time of injury. PE: mild effusion, tenderness over medial " +
      "joint line. +McMurray on R, +Valgus stress with pain (no laxity). Lachman negative. " +
      "Working dx: medial meniscus tear. MRI ordered. Ice, NSAIDs, profile L1 no running x 2 weeks. " +
      "Ortho referral placed. Follow up post-MRI."
  },
  {
    label: 'BH — Anxiety/Sleep',
    age: 31, gender: 'F', encounter: 'established',
    notes:
      "31 yo female AD seen for f/u behavioral health. PHQ-9 today 14 (mod), GAD-7 16 (severe). " +
      "Endorses worsening anxiety x 3 weeks following PCS notification. Sleep 4-5 hrs, " +
      "early morning awakening. Denies SI/HI. No new stressors beyond PCS. " +
      "Currently on sertraline 50mg daily x 6 weeks — tolerating well, no GI side effects. " +
      "Increased sertraline to 100mg daily. Added trazodone 50mg qHS for sleep. " +
      "Discussed sleep hygiene, breathing exercises. BH referral for CBT, expedited. " +
      "Follow up in 4 weeks, sooner if worsening."
  },
];

const DemoModeSeeder = ({ setNotes, setPatientAge, setPatientGender, setEncounterType }) => {
  const [enabled, setEnabled] = React.useState(() => {
    try { return localStorage.getItem('writeright_demo_mode') === '1'; }
    catch { return false; }
  });
  const [seeded, setSeeded] = React.useState(false);
  const [idx, setIdx] = React.useState(0);

  React.useEffect(() => {
    const onToggle = (e) => setEnabled(!!e.detail?.enabled);
    window.addEventListener('writeright:demo-mode-toggle', onToggle);
    return () => window.removeEventListener('writeright:demo-mode-toggle', onToggle);
  }, []);

  if (!enabled) return null;

  const seed = () => {
    const scn = DEMO_SCENARIOS[idx];
    if (setPatientAge) setPatientAge(scn.age);
    if (setPatientGender) setPatientGender(scn.gender);
    if (setEncounterType) setEncounterType(scn.encounter);
    if (setNotes) setNotes(scn.notes);
    setSeeded(true);
    setTimeout(() => setSeeded(false), 2500);
    // Advance to next scenario for next click
    setIdx((idx + 1) % DEMO_SCENARIOS.length);
  };

  const current = DEMO_SCENARIOS[idx];
  return (
    <div
      className="flex items-center gap-2 -mt-1 pb-2 border-b border-purple-500/30 bg-purple-500/5 flex-wrap"
      data-testid="demo-mode-seeder-row"
    >
      <span className="text-[10px] font-bold uppercase tracking-wider text-purple-300 bg-purple-500/15 border border-purple-500/40 rounded px-1.5 py-0.5">
        Demo
      </span>
      <button
        type="button"
        onClick={seed}
        className={`text-[11px] font-semibold px-2.5 py-1 rounded border transition-all ${
          seeded
            ? 'border-emerald-500/60 bg-emerald-500/15 text-emerald-200'
            : 'border-purple-500/40 bg-purple-500/10 text-purple-200 hover:bg-purple-500/20 hover:border-purple-500/60'
        }`}
        data-testid="demo-mode-seed-btn"
        title={`Tap to seed: ${current.label}. Next tap rotates to the next scenario.`}
      >
        {seeded ? 'Demo seeded ✓' : `Seed: ${current.label}`}
      </button>
      <span className="text-[10px] text-muted-foreground italic">
        Scenario {idx + 1} of {DEMO_SCENARIOS.length} — taps cycle
      </span>
    </div>
  );
};

export default ProviderInputCard;

/**
 * MedicModeRow — Per-encounter toggle + smart "Save as default" affordance.
 * Feb 26, 2026.
 *
 * Shows the standard Medic Mode checkbox. When the user flips it to a value
 * that differs from their profile default (`user.default_medic_mode`), a
 * small "Save as default" pill fades in for 6 seconds. One tap PUTs the
 * new value to /api/auth/profile so subsequent encounters start in that
 * mode. After save, the pill shows "Saved as default ✓" briefly, then
 * disappears.
 *
 * If `user` is null (guest mode) the offer pill is hidden — guests can't
 * have a saved default.
 */
const MedicModeRow = ({ medicMode, setMedicMode, user }) => {
  const [offerVisible, setOfferVisible] = React.useState(false);
  const [savedFlash, setSavedFlash] = React.useState(false);
  const [savingTo, setSavingTo] = React.useState(null);
  const dismissTimerRef = React.useRef(null);

  const profileDefault = !!(user && user.default_medic_mode);
  const isLoggedIn = !!user;

  const handleToggle = (next) => {
    setMedicMode(next);
    // Show the offer pill only when (a) logged in and (b) new value differs
    // from profile default. If it matches the profile default, no offer
    // needed.
    if (!isLoggedIn) return;
    if (next === profileDefault) {
      setOfferVisible(false);
      return;
    }
    setOfferVisible(true);
    setSavedFlash(false);
    if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current);
    dismissTimerRef.current = setTimeout(() => setOfferVisible(false), 6000);
  };

  const handleSaveAsDefault = async () => {
    if (savingTo !== null) return;
    setSavingTo(medicMode);
    try {
      const API = window.__WR_API_BASE__;
      await axios.put(`${API}/api/auth/profile`, { default_medic_mode: medicMode }, { withCredentials: true });
      setSavedFlash(true);
      toast.success(`Medic Mode default set to ${medicMode ? 'ON' : 'OFF'}.`);
      // Mutate the user object in-place so the offer logic stops re-firing
      if (user) user.default_medic_mode = medicMode;
      setTimeout(() => { setOfferVisible(false); setSavedFlash(false); }, 2000);
    } catch (e) {
      toast.error('Could not save default — try again from Profile Settings.');
    } finally {
      setSavingTo(null);
    }
  };

  React.useEffect(() => {
    return () => { if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current); };
  }, []);

  return (
    <div
      className={`flex items-center gap-2 -mt-1 pb-2 border-b flex-wrap ${medicMode ? 'border-amber-500/40 bg-amber-500/5' : 'border-border/50'}`}
      data-testid="input-medic-mode-row"
    >
      <input
        id="input-medic-mode-toggle"
        type="checkbox"
        checked={!!medicMode}
        onChange={(e) => handleToggle(!!e.target.checked)}
        className="h-3.5 w-3.5 cursor-pointer accent-amber-500"
        data-testid="input-medic-mode-toggle"
      />
      <Label htmlFor="input-medic-mode-toggle" className="text-[11px] font-semibold cursor-pointer select-none">
        <span className={medicMode ? 'text-amber-300' : 'text-muted-foreground'}>Medic Mode</span>
        <span className="ml-1 text-[10px] text-muted-foreground font-normal">
          HPI + ROS + ICD-10 handoff (no A/P, no Education) — for medic/68W → provider pass-the-baton
        </span>
      </Label>
      {medicMode && (
        <span
          className="text-[9px] font-bold text-amber-200 bg-amber-500/20 border border-amber-500/40 rounded px-1.5 py-0.5"
          data-testid="input-medic-mode-tag"
        >
          MEDIC
        </span>
      )}
      {offerVisible && isLoggedIn && (
        <button
          type="button"
          onClick={handleSaveAsDefault}
          disabled={savingTo !== null}
          className={`ml-1 text-[10px] font-semibold px-2 py-0.5 rounded border transition-all animate-in fade-in slide-in-from-left-2 ${
            savedFlash
              ? 'border-emerald-500/60 bg-emerald-500/15 text-emerald-200'
              : 'border-blue-500/40 bg-blue-500/10 text-blue-300 hover:bg-blue-500/20 hover:border-blue-500/60'
          } disabled:opacity-60`}
          data-testid="medic-mode-save-default-btn"
          title={savedFlash ? 'Saved' : `Make ${medicMode ? 'ON' : 'OFF'} the default for new encounters`}
        >
          {savedFlash ? 'Saved as default ✓' : `Save as default (${medicMode ? 'ON' : 'OFF'})`}
        </button>
      )}
    </div>
  );
};
